import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Layers, TrendingUp, Wallet, ShoppingBag, Globe } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#47FFB0', '#4B9DFF', '#FFC107', '#FF6B6B', '#A78BFA', '#F97316'];

const BuyerHoldingsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { fetchHoldings(); }, []);

  const fetchHoldings = async () => {
    try {
      const res = await axios.get(`${API}/buyer/holdings`);
      setData(res.data);
    } catch (error) {
      console.error('Error fetching holdings:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#0B0B0B' }}>Loading holdings...</div>;
  }

  const totals = data?.totals || {};
  const allocation = data?.allocation || [];
  const hasHoldings = totals.total_nfts > 0;

  // Prepare pie chart data
  const pieData = allocation.filter(a => a.nft_count > 0).map(a => ({
    name: a.portfolio_name,
    value: a.nft_count,
  }));

  return (
    <div data-testid="buyer-holdings-page" style={{ padding: '40px' }}>
      <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#0B0B0B', marginBottom: '8px' }}>Holdings</h1>
      <p style={{ fontSize: '16px', color: '#999', marginBottom: '32px' }}>Your portfolio overview</p>

      {!hasHoldings ? (
        <div style={{ background: '#FFFFFF', borderRadius: '16px', padding: '60px', textAlign: 'center', border: '1px solid #E6E8EF' }}>
          <Layers size={48} color="#4B9DFF" style={{ marginBottom: '16px' }} />
          <h3 style={{ color: '#0B0B0B', fontSize: '20px', marginBottom: '8px' }}>No holdings yet</h3>
          <p style={{ color: '#666', fontSize: '14px', maxWidth: '400px', margin: '0 auto 24px' }}>
            Browse the marketplace to purchase NFTs or sync your wallet to import on-chain assets.
          </p>
          <button
            data-testid="browse-marketplace-btn"
            onClick={() => navigate('/buyer/marketplace')}
            style={{ padding: '12px 24px', background: '#4B9DFF', color: '#FFF', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
          >
            Browse Marketplace
          </button>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '32px' }}>
            {[
              { label: 'Total NFTs', value: totals.total_nfts, icon: Layers, color: '#4B9DFF' },
              { label: 'Portfolios', value: totals.total_portfolios, icon: TrendingUp, color: '#47FFB0' },
              { label: 'Purchased', value: totals.purchased_nfts, icon: ShoppingBag, color: '#FFC107' },
              { label: 'On-Chain', value: totals.onchain_nfts, icon: Globe, color: '#A78BFA' },
              { label: 'Cost Basis', value: `$${totals.cost_basis_usd?.toLocaleString() || '0'}`, icon: Wallet, color: '#FF6B6B' },
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} data-testid={`holdings-kpi-${label.toLowerCase().replace(/\s/g, '-')}`} style={{
                background: '#FFFFFF', borderRadius: '12px', padding: '20px', border: '1px solid #E6E8EF',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <Icon size={18} color={color} />
                  <span style={{ color: '#999', fontSize: '13px' }}>{label}</span>
                </div>
                <p style={{ color, fontSize: '28px', fontWeight: 700 }}>{value}</p>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            {/* Pie Chart */}
            <div style={{ background: '#FFFFFF', borderRadius: '12px', padding: '24px', border: '1px solid #E6E8EF' }}>
              <h3 style={{ color: '#4B9DFF', fontSize: '16px', marginBottom: '16px' }}>Allocation by Portfolio</h3>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value"
                      label={({ name, value }) => `${name.slice(0, 15)}: ${value}`}>
                      {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #E6E8EF', borderRadius: '8px', color: '#0B0B0B' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p style={{ color: '#666', textAlign: 'center', padding: '40px 0' }}>No allocation data</p>
              )}
            </div>

            {/* Allocation List */}
            <div style={{ background: '#FFFFFF', borderRadius: '12px', padding: '24px', border: '1px solid #E6E8EF' }}>
              <h3 style={{ color: '#4B9DFF', fontSize: '16px', marginBottom: '16px' }}>Portfolio Breakdown</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {allocation.map((a, i) => (
                  <div key={a.portfolio_id} data-testid={`allocation-row-${i}`} style={{
                    padding: '14px', background: '#FFFFFF', borderRadius: '8px', border: '1px solid #E6E8EF',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: COLORS[i % COLORS.length] }} />
                      <div>
                        <p style={{ color: '#0B0B0B', fontSize: '14px', fontWeight: 600 }}>{a.portfolio_name}</p>
                        <p style={{ color: '#666', fontSize: '12px' }}>{a.nft_count} NFTs</p>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {a.estimated_value != null ? (
                        <p style={{ color: '#47FFB0', fontSize: '14px', fontWeight: 600 }}>${a.estimated_value.toLocaleString()}</p>
                      ) : (
                        <p style={{ color: '#666', fontSize: '12px' }}>N/A</p>
                      )}
                      {a.unit_price > 0 && (
                        <p style={{ color: '#999', fontSize: '11px' }}>@ ${a.unit_price}/unit</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BuyerHoldingsPage;
